import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Typography,
  Paper,
  Grid,
  Table,
  TableRow,
  TableBody,
  TableHead,
  IconButton,
  Toolbar,
  Icon,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MUITablePagination from "../HOC/MUI/TablePagination/MUITablePagination";
import { Add, Delete, Edit } from "@mui/icons-material";
import clsx from "clsx";
import EmptyTableMessage from "../HOC/FunctionComponents/EmptyTableMessage";
import { FixedTableCell } from "../HOC/CustomComponents/FixedTableCell";
import TableFixedHeaderWraper from "../HOC/CustomComponents/TableWithFixedHeader";
import SpanLink from "../HOC/CustomComponents/SpanLink";

const PREFIX = "BranchesTable";

const classes = {
  p: `${PREFIX}-p`,
  tap: `${PREFIX}-tap`,
  table: `${PREFIX}-table`,
  tableContainer: `${PREFIX}-tableContainer`,
  title: `${PREFIX}-title`,
  toolbar: `${PREFIX}-toolbar`,
  focus: `${PREFIX}-focus`,
  destinations: `${PREFIX}-destinations`,
  iconColor: `${PREFIX}-iconColor`,
  deleteIcon: `${PREFIX}-deleteIcon`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.p}`]: {
    fontSize: "14px",
    margin: "2px 1px",
    width: 100,
  },

  [`& .${classes.tap}`]: {
    margin: 0,
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    // flexDirection: "column",
    textTransform: "capitalize",
  },

  [`& .${classes.table}`]: {
    display: "grid",
  },

  [`& .${classes.tableContainer}`]: {
    minHeight: "250px",
  },

  [`& .${classes.title}`]: {
    flex: 1,
  },

  [`& .${classes.toolbar}`]: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    borderBottom: "1px solid " + theme.palette.divider,
    borderRight: "1px solid " + theme.palette.divider,
  },

  [`& .${classes.focus}`]: {
    backgroundColor: theme.palette.action.hover,
  },

  [`& .${classes.destinations}`]: {
    [theme.breakpoints.down("md")]: {
      marginTop: theme.spacing(2),
    },
  },

  [`& .${classes.iconColor}`]: {
    color: theme.palette.success.main,
  },

  [`& .${classes.deleteIcon}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const BranchesTable = (props) => {
  const {
    branches = [],
    addBranches,
    setBranchesList,
    branchError,
    setBranchError,
  } = props;
  const { t } = useTranslation();
  const [branchesIndex, setBranchesIndex] = useState(0);
  const [branchesPage, setBranchesPage] = useState(0);
  const [branchesRowsPerPage, setBranchesRowsPerPage] = useState(20);

  const handleBranchesPage = (event, newPage) => {
    setBranchesPage(newPage);
  };
  const handleBranchesRowsPerPage = (event) => {
    setBranchesRowsPerPage(parseInt(event.target.value));
    setBranchesPage(0);
  };

  function updateErrorIndex(index) {
    setBranchError((prevErrors) => {
      const newErrors = {};
      Object.keys(prevErrors).forEach((key) => {
        const match = key.match(/input\.branches\.(\d+)\.branchId/);
        if (match) {
          const errorIndex = parseInt(match[1], 10);
          if (errorIndex > index) {
            newErrors[`input.branches.${errorIndex - 1}.branchId`] =
              prevErrors[key];
          } else if (errorIndex < index) {
            newErrors[key] = prevErrors[key];
          }
        }
      });
      return newErrors;
    });
  }
  return (
    <StyledGrid container px={2} alignItems="flex-start">
      <Grid container item sm={12} component={Paper}>
        <Toolbar className={classes.toolbar} variant="dense">
          <Typography className={classes.title} color="inherit" variant="h6">
            {t("branches")}
          </Typography>

          {addBranches ? (
            <IconButton
              color="primary"
              onClick={() => {
                addBranches();
                setBranchesIndex(0);
              }}
              aria-label="Add branches"
              size="large"
            >
              <Add />
            </IconButton>
          ) : null}
        </Toolbar>
        <Grid container item className={classes.table}>
          {branches.length <= 0 ? (
            <EmptyTableMessage loading={false} message={t("noBranches")} />
          ) : (
            <TableFixedHeaderWraper className={classes.tableContainer}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <FixedTableCell>{t("branch")}</FixedTableCell>
                    <FixedTableCell>{t("default")}</FixedTableCell>
                    {addBranches ? <FixedTableCell></FixedTableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branches.length > 0 &&
                    (branchesRowsPerPage > 0
                      ? branches.slice(
                          branchesPage * branchesRowsPerPage,
                          branchesPage * branchesRowsPerPage +
                            branchesRowsPerPage
                        )
                      : branches
                    ).map((row, index) => (
                      <TableRow
                        className={clsx({
                          [classes.focus]:
                            branchesPage * branchesRowsPerPage + index ===
                            branchesIndex,
                        })}
                        hover
                        key={index}
                        onClick={() => {
                          setBranchesPage(0);
                          setBranchesIndex(
                            branchesPage * branchesRowsPerPage + index
                          );
                        }}
                      >
                        <FixedTableCell>
                          <SpanLink
                            pathname={`/admin/branches/${row?.branch?.id}`}
                          >
                            {row.branch.name ?? row.branch.name}
                          </SpanLink>
                          {branchError && (
                            <Typography color="error" variant="caption">
                              {
                                branchError[
                                  `input.branches.${
                                    branchesPage * branchesRowsPerPage + index
                                  }.branchId`
                                ]
                              }
                            </Typography>
                          )}
                        </FixedTableCell>
                        <FixedTableCell>
                          {row?.isDefault && (
                            <Icon className={classes.iconColor}>
                              check_circle_outline
                            </Icon>
                          )}
                        </FixedTableCell>
                        {addBranches ? (
                          <FixedTableCell>
                            <IconButton
                              size="small"
                              onClick={() =>
                                addBranches(
                                  branchesPage * branchesRowsPerPage + index
                                )
                              }
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={classes.deleteIcon}
                              onClick={() =>
                                setBranchesList((prev) => {
                                  const filterd = prev.filter(
                                    (i, deleteIndex) => deleteIndex !== index
                                  );
                                  updateErrorIndex(index);
                                  return filterd;
                                })
                              }
                            >
                              <Delete />
                            </IconButton>
                          </FixedTableCell>
                        ) : null}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableFixedHeaderWraper>
          )}
          <MUITablePagination
            count={branches?.length ?? 0}
            rowsPerPage={branchesRowsPerPage}
            page={branchesPage}
            onPageChange={handleBranchesPage}
            onRowsPerPageChange={handleBranchesRowsPerPage}
          />
        </Grid>
      </Grid>
    </StyledGrid>
  );
};

export default BranchesTable;
